import './table-chart.scss';
import {
  Box,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableBody,
  useTheme,
  Typography,
  Avatar,
} from '@mui/material';
import {useEffect, useState} from 'react';
import {ChartCard} from '../chart-card/chart-card';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../i18n/translations';
import {avatarSizesAndCoefficients} from '../../../utils/constants/avatar-sizes-and-coefficients';
import {isSvg} from '../../../utils/functions/validate-uri-svg';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import SouthIcon from '@mui/icons-material/South';
import NorthIcon from '@mui/icons-material/North';
import {ErrorBox} from '../../error-box/error-box';

export interface TableChartRow {
  id: string;
  columns: Array<{
    columnId: string;
    value: string | number;
    avatarUrl?: string;
  }>;
}

export type TableChartRows = Array<TableChartRow>;

enum SortType {
  Ascending = 'ascending',
  Descending = 'descending',
}

/**
 * Table chart used in the statistics dashboards.
 */
export const TableChart = (props: {
  rows: TableChartRows;
  title: string;
  columns: Array<{
    width: string;
    columnId: string;
    title: string;
    enableSort: boolean;
    unitLabel?: string;
    avatar?: {
      type: 'user' | 'entity' | 'other';
      isIcon?: boolean;
    };
  }>;
  loading: boolean;
  handleRefresh: () => void;
  subtitle?: string;
  id?: string;
  error?: string;
}) => {
  // Hold state
  const [rows, setRows] = useState<TableChartRows>(props.rows);
  const [currentSort, setCurrentSort] = useState<string | undefined>();

  // Get theme
  const theme = useTheme();

  // Translations
  const {t} = useTranslation();

  // Update rows
  useEffect(() => {
    // Reset value
    setCurrentSort(undefined);

    // Set rows
    setRows(props.rows);
  }, [props.rows]);

  // Sort
  const handleSort = (columnId: string) => {
    // Select the sort type
    let sortType: SortType | undefined = SortType.Descending;
    if (currentSort) {
      if (currentSort.search(columnId) !== -1) {
        if (currentSort.search(SortType.Descending) !== -1) {
          sortType = SortType.Ascending;
        } else {
          // It's ascending, remove the sort for the column
          sortType = undefined;
        }
      }
    }

    // Sort
    const sortedRows: Array<TableChartRow> = [...props.rows];
    if (sortType === SortType.Descending) {
      sortedRows.sort((a, b) => {
        const valueA: string | number =
          a.columns.find((obj) => obj.columnId === columnId)?.value || '';
        const valueB: string | number =
          b.columns.find((obj) => obj.columnId === columnId)?.value || '';

        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return valueB - valueA;
        } else {
          return valueB
            .toString()
            .toLocaleLowerCase()
            .localeCompare(valueA.toString().toLocaleLowerCase());
        }
      });
    } else if (sortType === SortType.Ascending) {
      sortedRows.sort((a, b) => {
        const valueA: string | number =
          a.columns.find((obj) => obj.columnId === columnId)?.value || '';
        const valueB: string | number =
          b.columns.find((obj) => obj.columnId === columnId)?.value || '';

        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return valueA - valueB;
        } else {
          return valueA
            .toString()
            .toLocaleLowerCase()
            .localeCompare(valueB.toString().toLocaleLowerCase());
        }
      });
    }

    // Set rows
    setRows(sortedRows);

    // Set sort
    if (sortType) {
      setCurrentSort(`${columnId}-${sortType}`);
    } else {
      setCurrentSort(undefined);
    }
  };

  // Render avatar
  const renderAvatar = (
    type: 'user' | 'entity' | 'other',
    isIcon: boolean,
    url?: string,
    alt?: string
  ) => {
    // Avatar sizes, coefficients and default icon
    const avatarSize: number = avatarSizesAndCoefficients.avatarSizes.small;
    const iconSize: number = avatarSize * avatarSizesAndCoefficients.iconSizeCoefficient;
    let defaultAvatarIcon: React.ReactElement = <></>;
    let avatarBorderRadius: string | undefined;
    switch (type) {
      case 'user':
        defaultAvatarIcon = <PersonIcon sx={{fontSize: iconSize}} />;
        avatarBorderRadius = `${avatarSize * avatarSizesAndCoefficients.borderRadiusCoefficients.circle}px`;
        break;
      case 'entity':
        defaultAvatarIcon = <BusinessIcon sx={{fontSize: iconSize}} />;
        avatarBorderRadius = `${
          avatarSize * avatarSizesAndCoefficients.borderRadiusCoefficients.roundedCornersLow
        }px`;
        break;
      default:
        defaultAvatarIcon = <></>;
        avatarBorderRadius = `${
          avatarSize * avatarSizesAndCoefficients.borderRadiusCoefficients.roundedCornersHigh
        }px`;
        break;
    }

    return isIcon ? (
      <Avatar
        className="table-chart-avatar-container"
        sx={{
          width: avatarSize,
          height: avatarSize,
          borderRadius: avatarBorderRadius,
          borderColor: theme.palette.action.disabled,
        }}
      >
        {url ? (
          isSvg(url) ? (
            <object
              className="table-chart-avatar-svg-object"
              data={url}
              aria-label={alt}
              type="image/svg+xml"
              width={`${iconSize}`}
              height={`${iconSize}`}
            ></object>
          ) : (
            <img
              style={{
                width: `${iconSize}px`,
                height: `${iconSize}px`,
              }}
              src={url}
              alt={alt}
            />
          )
        ) : (
          defaultAvatarIcon
        )}
      </Avatar>
    ) : (
      <Avatar
        className="table-chart-avatar-container"
        src={url}
        alt={alt}
        sx={{
          width: avatarSize,
          height: avatarSize,
          borderRadius: avatarBorderRadius,
          borderColor: theme.palette.action.disabled,
        }}
      >
        {defaultAvatarIcon}
      </Avatar>
    );
  };

  return (
    <ChartCard
      title={props.title}
      subtitle={props.subtitle}
      loading={props.loading}
      handleRefresh={props.handleRefresh}
    >
      <TableContainer className="table-chart-container">
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {props.columns.map((column) => {
                return (
                  <TableCell
                    key={`table-chart-${props.id}-head-${column.columnId}`}
                    className="table-chart-title-cell"
                    sx={{
                      width: column.width,
                    }}
                  >
                    <Box
                      className="table-chart-title-container"
                      sx={{
                        cursor: props.loading || !column.enableSort ? undefined : 'pointer',
                        '&:hover': {
                          backgroundColor:
                            props.loading || !column.enableSort
                              ? undefined
                              : theme.palette.action.hover,
                        },
                      }}
                      onClick={
                        props.loading || !column.enableSort
                          ? undefined
                          : () => handleSort(column.columnId)
                      }
                    >
                      {column.unitLabel ? `${column.title} (${column.unitLabel})` : column.title}
                      {column.enableSort &&
                        currentSort &&
                        currentSort?.search(column.columnId) !== -1 && (
                          <Box className="table-chart-title-order-icon-container">
                            {currentSort.search(SortType.Descending) !== -1 ? (
                              <NorthIcon className="table-chart-title-order-icon" color="primary" />
                            ) : (
                              <SouthIcon className="table-chart-title-order-icon" color="primary" />
                            )}
                          </Box>
                        )}
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          {rows.length > 0 && !props.error && (
            <TableBody>
              {rows.map((row) => {
                return (
                  <TableRow key={`table-chart-${props.id}-row-${row.id}`}>
                    {props.columns.map((column) => {
                      // Get the column data
                      const columnData:
                        | {
                            columnId: string;
                            value: string | number;
                            avatarUrl?: string;
                          }
                        | undefined = row.columns.find((obj) => obj.columnId === column.columnId);

                      // Process the column value
                      let processedValue: string = '';
                      if (columnData?.value !== undefined && columnData.value !== null) {
                        if (typeof columnData.value === 'number') {
                          processedValue = column.unitLabel
                            ? columnData.value.toFixed(2)
                            : columnData.value.toFixed(0);
                        } else {
                          processedValue = columnData.value.toString();
                        }
                      }

                      return (
                        <TableCell
                          key={`table-chart-${props.id || props.title}-row-${row.id}-head-${
                            column.columnId
                          }`}
                          sx={{width: column.width}}
                        >
                          <Box className="table-chart-body-cell-content-container">
                            {column.avatar
                              ? renderAvatar(
                                  column.avatar.type,
                                  column.avatar.isIcon === true ? true : false,
                                  columnData?.avatarUrl,
                                  processedValue
                                )
                              : undefined}
                            {processedValue}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          )}
        </Table>
        {rows.length === 0 && !props.error && (
          <Typography
            variant="subtitle2"
            component="span"
            color="text.secondary"
            className="table-chart-no-data-message"
          >
            {t(translations.charts.no_data)}
          </Typography>
        )}
        {props.error && (
          <Box className="table-chart-error-box">
            <ErrorBox error={props.error} />
          </Box>
        )}
      </TableContainer>
    </ChartCard>
  );
};
